import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi, type ConsolidationListItem, type ConsolidationDetail } from '@/api/cargo';
import { PageHead, DataTable, Drawer, SectionCard, Field, StatusBadge, type Col } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { num } from '@/shared/format';
import { TRANSPORT_MODE, SHIPMENT_STATUS_COLOR, toOptions } from '@/shared/cargo';

export function ConsolidationsPage() {
  const toast = useToast();
  const [editId, setEditId] = useState<string | null | undefined>(undefined);
  const q = useQuery({ queryKey: ['cargo', 'consolidations'], queryFn: cargoApi.consolidations });

  const cols: Col<ConsolidationListItem>[] = [
    { key: 'ConsolidationNo', label: 'Дугаар', width: 160, render: (r) => <button className="ct-link" onClick={(e) => { e.stopPropagation(); setEditId(r.Id); }}>{r.ConsolidationNo}</button> },
    { key: 'TransportMode', label: 'Тээвэр', render: (r) => r.TransportModeLabel },
    { key: 'route', label: 'Маршрут', render: (r) => `${r.FromWarehouseName ?? '—'} → ${r.ToWarehouseName ?? '—'}` },
    { key: 'Status', label: 'Төлөв', render: (r) => <StatusBadge label={r.StatusLabel} color="var(--ts-info)" /> },
    { key: 'ShipmentCount', label: 'Илгээмж', align: 'right', render: (r) => r.ShipmentCount },
    { key: 'TotalWeightKg', label: 'Жин (кг)', align: 'right', render: (r) => num(r.TotalWeightKg) },
  ];

  return (
    <div>
      <PageHead title="Нэгтгэл" subtitle="Илгээмжүүдийг багцлан тээвэрлэх."
        actions={<button className="ts-btn ts-btn-primary" onClick={() => setEditId(null)}><i className="bi bi-plus-lg" /> Шинэ нэгтгэл</button>} />
      <DataTable cols={cols} rows={q.data ?? []} onRowClick={(r) => setEditId(r.Id)} empty="Нэгтгэл алга." />
      {editId !== undefined && (
        <ConsolidationDrawer id={editId} onClose={() => setEditId(undefined)} onChanged={() => q.refetch()} toast={toast} />
      )}
    </div>
  );
}

function ConsolidationDrawer({ id, onClose, onChanged, toast }: { id: string | null; onClose: () => void; onChanged: () => void; toast: (m: string, t?: 'info' | 'success' | 'warning' | 'danger') => void }) {
  const isNew = id === null;
  const [detail, setDetail] = useState<ConsolidationDetail | null>(null);
  const [form, setForm] = useState({ TransportMode: 0, FKFromWarehouseId: '', FKToWarehouseId: '', Note: '' });
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const warehousesQ = useQuery({ queryKey: ['cargo', 'warehouses'], queryFn: cargoApi.warehouses });

  useEffect(() => {
    if (!isNew && id) cargoApi.consolidation(id).then((d) => { setDetail(d); setForm({ TransportMode: d.TransportMode, FKFromWarehouseId: d.FKFromWarehouseId ?? '', FKToWarehouseId: d.FKToWarehouseId ?? '', Note: d.Note ?? '' }); });
  }, [id, isNew]);

  async function save() {
    setBusy(true);
    try {
      const d = await cargoApi.saveConsolidation(id, { ...form, FKFromWarehouseId: form.FKFromWarehouseId || null, FKToWarehouseId: form.FKToWarehouseId || null });
      setDetail(d);
      toast('Хадгаллаа.', 'success');
      onChanged();
      if (isNew) onClose();
    } catch (e) { toast((e as Error).message, 'danger'); } finally { setBusy(false); }
  }
  async function act(fn: () => Promise<ConsolidationDetail>, msg: string) {
    setBusy(true);
    try { setDetail(await fn()); toast(msg, 'success'); onChanged(); } catch (e) { toast((e as Error).message, 'danger'); } finally { setBusy(false); }
  }

  return (
    <Drawer title={isNew ? 'Шинэ нэгтгэл' : detail?.ConsolidationNo ?? 'Нэгтгэл'} open onClose={onClose}
      footer={<><button className="ts-btn ts-btn-ghost" onClick={onClose}>Хаах</button><button className="ts-btn ts-btn-primary" disabled={busy} onClick={save}><i className="bi bi-check2" /> Хадгалах</button></>}>
      <SectionCard icon="bi-boxes" title="Маршрут & тээвэр">
        <div className="ts-row">
          <div style={{ gridColumn: 'span 4' }}><Field label="Тээвэр"><select className="ts-select" value={form.TransportMode} onChange={(e) => setForm({ ...form, TransportMode: Number(e.target.value) })}>{toOptions(TRANSPORT_MODE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field></div>
          <div style={{ gridColumn: 'span 4' }}><Field label="Гарах агуулах"><select className="ts-select" value={form.FKFromWarehouseId} onChange={(e) => setForm({ ...form, FKFromWarehouseId: e.target.value })}><option value="">—</option>{warehousesQ.data?.map((w) => <option key={w.Id} value={w.Id}>{w.Name}</option>)}</select></Field></div>
          <div style={{ gridColumn: 'span 4' }}><Field label="Очих агуулах"><select className="ts-select" value={form.FKToWarehouseId} onChange={(e) => setForm({ ...form, FKToWarehouseId: e.target.value })}><option value="">—</option>{warehousesQ.data?.map((w) => <option key={w.Id} value={w.Id}>{w.Name}</option>)}</select></Field></div>
          <div style={{ gridColumn: 'span 12' }}><Field label="Тэмдэглэл"><input className="ts-input" value={form.Note} onChange={(e) => setForm({ ...form, Note: e.target.value })} /></Field></div>
        </div>
      </SectionCard>

      {detail && (
        <SectionCard icon="bi-box-seam" title={`Илгээмжүүд (${detail.Shipments.length})`} hint={detail.StatusLabel}>
          <div className="ts-flex-row" style={{ marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
            <button className="ts-btn" disabled={busy} onClick={() => setPicker(true)}><i className="bi bi-plus-lg" /> Илгээмж нэмэх</button>
            <button className="ts-btn" disabled={busy} onClick={() => act(() => cargoApi.depart(detail.Id), 'Гарлаа.')}><i className="bi bi-airplane" /> Гарсан</button>
            <button className="ts-btn" disabled={busy} onClick={() => act(() => cargoApi.arrive(detail.Id), 'Ирлээ.')}><i className="bi bi-geo-alt" /> Ирсэн</button>
          </div>
          {detail.Shipments.map((s) => (
            <div key={s.Id} className="ts-flex-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--ts-border)' }}>
              <span><i className="bi bi-box" /> {s.TrackingNo} · {s.CustomerName}</span>
              <span className="ts-flex-row">
                <StatusBadge label={s.StatusLabel} color={SHIPMENT_STATUS_COLOR[s.Status]} />
                <button className="ct-link" onClick={() => act(() => cargoApi.removeShipments(detail.Id, [s.Id]), 'Хаслаа.')}><i className="bi bi-x-circle" /></button>
              </span>
            </div>
          ))}
          {!detail.Shipments.length && <div className="ts-page-sub">Илгээмж нэмэгдээгүй.</div>}
        </SectionCard>
      )}

      {picker && detail && (
        <ShipmentPicker excludeIds={detail.Shipments.map((s) => s.Id)} onClose={() => setPicker(false)} onAdd={(ids) => { setPicker(false); act(() => cargoApi.addShipments(detail.Id, ids), 'Нэмлээ.'); }} />
      )}
    </Drawer>
  );
}

function ShipmentPicker({ excludeIds, onClose, onAdd }: { excludeIds: string[]; onClose: () => void; onAdd: (ids: string[]) => void }) {
  const [sel, setSel] = useState<string[]>([]);
  const q = useQuery({ queryKey: ['cargo', 'shipments', ''], queryFn: () => cargoApi.shipments() });
  const avail = (q.data ?? []).filter((s) => !excludeIds.includes(s.Id) && s.Status <= 3);
  return (
    <Drawer title="Илгээмж сонгох" open onClose={onClose}
      footer={<><button className="ts-btn ts-btn-ghost" onClick={onClose}>Болих</button><button className="ts-btn ts-btn-primary" disabled={!sel.length} onClick={() => onAdd(sel)}>Нэмэх ({sel.length})</button></>}>
      <div className="ts-card if-card-body">
        {avail.map((s) => (
          <label key={s.Id} className="ts-flex-row" style={{ padding: '6px 0', cursor: 'pointer', borderBottom: '1px solid var(--ts-border)' }}>
            <input type="checkbox" checked={sel.includes(s.Id)} onChange={(e) => setSel((p) => (e.target.checked ? [...p, s.Id] : p.filter((x) => x !== s.Id)))} />
            <span>{s.TrackingNo} · {s.CustomerName} · {s.StatusLabel}</span>
          </label>
        ))}
        {!avail.length && <div className="ts-page-sub">Нэмэх боломжтой илгээмж алга.</div>}
      </div>
    </Drawer>
  );
}
